<template>
  <a
    href="#main"
    @click.prevent="focusMainContent"
    class="[&:not(:focus)]:sr-only absolute top-0 left-0 z-[999] bg-blue-600 text-white font-medium px-3 py-1 shadow-lg rounded-br-lg underline underline-offset-2 decoration-blue-200/70"
  >
    {{ t('skipToContent') }}
  </a>
  <div
    class="grid grid-cols-1 items-stretch h-device w-full transition-all duration-200"
    :class="pageWrapperStyles"
  >
    <div
      ref="navWrapper"
      class="flex-1 min-h-0 h-full col-start-1 col-end-2 row-start-1 row-end-2 flex flex-col z-50 w-full max-w-[66%] min-w-[256px] xl:min-w-[300px] text-white transition-[visibility,transform] duration-200 select-none"
      :class="[
        admin ? 'bg-s-red-1000' : 'bg-black',
        sidebarOpen
          ? 'visible pointer-events-auto ease-out'
          : '-translate-x-full invisible pointer-events-none ease-in',
      ]"
      :aria-hidden="!sidebarOpen"
      tabindex="-1"
    >
      <div
        class="relative w-full px-3 py-[0.8125rem] flex flex-row-reverse items-center justify-between"
        :class="
          admin
            ? 'bg-gradient-to-b from-s-red-500/30 to-s-red-600/0'
            : 'bg-gradient-to-b from-blue-1000 to-transparent'
        "
      >
        <user-menu />
      </div>
      <hr
        aria-hidden="true"
        class="relative border-t-[1px]"
        :class="admin ? 'border-s-red-500/40' : 'border-blue-400/40'"
      />
      <institution-selector v-if="!admin" />
      <div class="relative flex-1 flex flex-col text-base pt-3 overflow-y-auto hide-scrollbar">
        <nav :aria-label="t('mainNavigation')" role="navigation" class="flex-1 flex flex-col">
          <sidebar-item v-for="item in props.items" :key="getKeyFor(item)" :item="item" />
        </nav>
        <div
          class="px-3 py-4"
          :class="
            admin
              ? 'bg-gradient-to-t from-s-red-600/30 to-s-red-600/0'
              : 'bg-gradient-to-t from-blue-1000 to-transparent'
          "
        >
          <s-link
            href="/"
            class="flex flex-col transition text-white hover:text-blue-100 mb-2"
            external
          >
            <s-logo class="logo h-10 w-auto" role="presentation" />
            <span class="sr-only">{{ t('stembleDashboard') }}</span>
          </s-link>
        </div>
      </div>
    </div>
    <div
      class="relative col-start-1 col-end-2 md:col-start-2 md:col-end-3 row-start-1 row-end-2 h-dvh w-full flex flex-col align-center bg-background"
    >
      <div
        class="absolute w-full md:w-0 h-full md:h-0 bg-slate-900/70 z-40 transition"
        aria-hidden="true"
        :class="
          sidebarOpen
            ? 'opacity-100 md:opacity-0 pointer-events-auto md:pointer-events-none'
            : 'opacity-0 pointer-events-none'
        "
        @click="closeSidebar"
      />

      <div class="flex w-full bg-white border-b border-gray-200 divide-x divide-gray-200">
        <button
          ref="sidebarToggleButton"
          @click="toggleSidebar"
          class="px-3 py-1.5 transition text-gray-500 hover:text-blue-500 focus:text-blue-500 hover:bg-gray-100 focus:bg-gray-100 shink-0 focus-visible:outline-blue-400 -outline-offset-2"
          :aria-expanded="sidebarOpen"
        >
          <s-icon :name="sidebarOpen ? 'backburger' : 'forwardburger'" size="22" />
          <span class="sr-only">{{ sidebarOpen ? t('closeSidebar') : t('openSidebar') }}</span>
        </button>
        <div class="flex flex-1 min-w-0">
          <s-breadcrumbs :breadcrumbs="breadcrumbs" />
        </div>
        <nav :aria-label="t('resources')" role="navigation" class="divide-x flex shink-0">
          <s-tooltip :text="t('helpAndInfo')" wrapperClass="flex-1 flex">
            <template #default="{tooltipId}">
              <s-dropdown-menu
                :items="[
                  {
                    label: t('links.contactSupport'),
                    href: '/support',
                    icon: 'help-circle',
                  },
                  {
                    label: t('links.termsAndConditionsLabel'),
                    href: t('links.termsAndConditions'),
                    external: true,
                    icon: 'file-document',
                  },
                  {
                    label: t('links.privacyPolicyLabel'),
                    href: t('links.privacyPolicy'),
                    external: true,
                    icon: 'shield-lock',
                  },
                  {
                    label: t('links.manageCookiesLabel'),
                    onClick: resetCookies,
                    icon: 'cookie',
                  },
                ]"
                dropdown-class="z-[60]"
                class="flex-1"
              >
                <template #trigger="{open}">
                  <button
                    class="flex-1 group/button flex items-center justify-center px-3 py-1.5 text-blue-400 hover:text-blue-600 hover:bg-gray-100 transition focus-visible:outline-blue-400 -outline-offset-2"
                    :aria-labelledby="tooltipId"
                  >
                    <s-icon :name="open ? 'close' : 'help-circle'" size="20" aria-hidden="true" />
                    <span class="sr-only">
                      {{ open ? t('actions.closeDropdownMenu') : t('actions.openDropdownMenu') }}
                    </span>
                  </button>
                </template>
              </s-dropdown-menu>
            </template>
          </s-tooltip>

          <s-tooltip :text="t('links.accessibilityLabel')" wrapperClass="flex-1 flex">
            <template #default="{tooltipId}">
              <s-link
                :href="t('links.accessibility')"
                class="flex-1 flex items-center justify-center bg-transparent px-3 py-1.5 text-blue-400 hover:text-blue-600 hover:bg-gray-100 transition focus-visible:outline-blue-400 -outline-offset-2"
                external
                :aria-labelledby="tooltipId"
              >
                <s-icon name="human" size="20" />
              </s-link>
            </template>
          </s-tooltip>

          <s-tooltip
            v-if="keyboardShortcuts.length"
            :text="t('keyboardShortcuts')"
            wrapperClass="flex-1 flex"
          >
            <template #default="{tooltipId}">
              <s-dropdown-menu
                :items="
                  keyboardShortcuts.map((shortcut, index) => ({
                    label: shortcut.label,
                    onClick: shortcut.onPress,
                    icon: shortcut.key,
                    index,
                  }))
                "
                dropdown-class="z-[60]"
                class="flex-1 flex"
              >
                <template #trigger="{open}">
                  <button
                    class="flex-1 group/button flex items-center justify-center px-3 py-1.5 text-blue-400 hover:text-blue-600 hover:bg-gray-100 transition focus-visible:outline-blue-400 -outline-offset-2"
                    :aria-labelledby="tooltipId"
                  >
                    <s-icon name="keyboard" size="22" />
                  </button>
                </template>
                <template #item="{item, active}">
                  <div class="flex items-center gap-2">
                    <span
                      class="keycap transition-all ease-out duration-150"
                      :class="[
                        active
                          ? '!text-blue-500 !border-blue-100 !bg-blue-50 !shadow-[0_3px_0_#81C8EA]'
                          : 'group-hover/button:!text-blue-400 group-focus/button:!text-blue-400',
                      ]"
                    >
                      {{ item.icon }}
                    </span>
                    {{ item.label }}
                  </div>
                </template>
              </s-dropdown-menu>
            </template>
          </s-tooltip>
          <s-tooltip
            v-else
            :text="t('noKeyboardShortcuts')"
            wrapperClass="flex flex-1 text-gray-300"
          >
            <template #default="{tooltipId}">
              <span
                class="flex items-center justify-center px-3 py-1.5"
                :aria-labelledby="tooltipId"
              >
                <s-icon name="keyboard" size="22" />
              </span>
            </template>
          </s-tooltip>

          <s-tooltip
            :text="t('notificationsCount', {count: page.props.notificationsCount})"
            wrapperClass="flex flex-1"
          >
            <template #default="{tooltipId}">
              <s-link
                :href="route('notifications.index')"
                class="flex-1 flex items-center justify-center relative px-3 py-1.5 hover:bg-gray-100 transition focus-visible:outline-blue-400 -outline-offset-2"
                :class="
                  page.props.notificationsCount > 0
                    ? 'text-s-red-200 hover:text-blue-600'
                    : 'text-gray-300 hover:text-blue-600'
                "
                :aria-labelledby="tooltipId"
              >
                <s-icon
                  :name="page.props.notificationsCount > 0 ? 'bell-alert' : 'bell'"
                  size="20"
                />
              </s-link>
            </template>
          </s-tooltip>
        </nav>
      </div>

      <s-flash-message />

      <main
        id="main"
        role="main"
        class="shrink min-h-0 h-full flex flex-col overflow-auto -outline-offset-2 focus-visible:outline-blue-400"
        scroll-region
        tabindex="-1"
      >
        <slot />
      </main>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  computed,
  defineProps,
  onBeforeUnmount,
  onMounted,
  PropType,
  provide,
  ref,
  Ref,
  watch,
} from 'vue';
import {SidebarItem as SidebarItemData} from './types';
import SidebarItem from './SidebarItem.vue';
import SFlashMessage from '../SFlashMessage.vue';
import SLink from '../SLink.vue';
import SLogo from '../../design-system/SLogo.vue';
import SBreadcrumbs from '../../design-system/SBreadcrumbs.vue';
import UserMenu from './UserMenu.vue';
import {route} from 'ziggy-js';
import {usePage} from '@inertiajs/vue3';
import {useI18n} from 'vue-i18n';
import {provideKeyboardShortcuts} from '../../composables/useKeyboardShortcut';
import {provideFlash} from '../../composables/useFlash';
import {useWindowSize} from '@vueuse/core';
import SIcon from '../../design-system/SIcon.vue';
import SDropdownMenu from '../../design-system/SDropdownMenu.vue';
import STooltip from '../../design-system/STooltip.vue';
import InstitutionSelector from './InstitutionSelector.vue';

const page = usePage<{notificationsCount: number}>();
const props = defineProps({
  admin: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array as PropType<SidebarItemData[]>,
    default() {
      return [];
    },
  },
});

const {t} = useI18n({
  inheritLocale: true,
  scope: 'local',
});

provideFlash();

const mobileSidebarBreakpoint = 768;
const autoCollapseWidth = 960;
const {width} = useWindowSize();
const isMobile = computed(() => width.value < mobileSidebarBreakpoint);
const shouldAutoCollapse = computed(() => width.value < autoCollapseWidth);
const sidebarOpen = ref(!isMobile.value);

watch(shouldAutoCollapse, (newShouldAutoCollapse) => {
  // When switching to desktop, open the sidebar
  // When switching to mobile, close the sidebar
  sidebarOpen.value = !newShouldAutoCollapse;
});

const getKeyFor = (item: SidebarItemData) => {
  if (item.type === 'divider') {
    return 'divider';
  }

  return item.label;
};

interface Breadcrumb {
  label: string;
  href: string;
  external?: boolean;
}

const breadcrumbs: Ref<Breadcrumb[]> = ref([]);
const updateBreadcrumbs = (newBreadcrumbs: Breadcrumb[]) => {
  breadcrumbs.value = newBreadcrumbs;
};
const routeHash = computed(() => {
  /**
   * Strip out params, slashes, and numbers so it's just one long string;
   * this way the breadcrumbs are only cleared on a 'real' page change
   */
  const pageUrlWithoutParams = page.url.split('?')[0];

  return pageUrlWithoutParams.replace(/\/\d+/g, '');
});
watch(routeHash, (newRouteHash) => {
  updateBreadcrumbs([]);
});

provide('breadcrumbs', {breadcrumbs, updateBreadcrumbs});

const pageWrapperStyles = computed(() => {
  return sidebarOpen.value
    ? 'md:grid-cols-[256px_1fr] xl:grid-cols-[300px_1fr]'
    : 'md:grid-cols-[0_1fr]';
});

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value;
  if (sidebarOpen.value) {
    setTimeout(() => navWrapper.value?.focus(), 50);
  }
};

const closeSidebar = () => {
  sidebarOpen.value = false;
  setTimeout(() => sidebarToggleButton.value?.focus(), 50);
};

const keyboardShortcuts = provideKeyboardShortcuts();

/**
 * # Focus Management: Mobile Sidebar Tabbing
 *
 * When the mobile sidebar nav is open, we want to close it
 * if the user switches focus to something outside of it.
 *
 * There are two cases where this will occur:
 * 1. the user clicks outside of it
 * 2. the user tabs out of it
 *
 * This is required to adhere to WCAG 2.2 guideline 2.4.11 and 2.4.12
 *
 * https://www.w3.org/TR/WCAG22/#focus-not-obscured-minimum
 */
const navWrapper = ref<HTMLElement | null>(null);
const sidebarToggleButton = ref<HTMLElement | null>(null);
const onFocusIn = (event: FocusEvent) => {
  if (!navWrapper.value || !isMobile.value) return;

  const focusHasLeftNav = !navWrapper.value.contains(event.target as Node);

  if (sidebarOpen.value && focusHasLeftNav) {
    sidebarOpen.value = false;
  }
};

// This will reset the cookies consent banner so the user can manage their selections.
const resetCookies = () => {
  (window as any).LaravelCookieConsent.reset();
};

const focusMainContent = () => {
  const mainElement = document.getElementById('main');
  if (mainElement) {
    mainElement.focus();
  }
};

onMounted(() => document.addEventListener('focusin', onFocusIn));
onBeforeUnmount(() => document.removeEventListener('focusin', onFocusIn));
</script>
<i18n>
{
  "en": {
    "closeSidebar": "Close Sidebar",
    "openSidebar": "Open Sidebar",
    "keyboardShortcuts": "Keyboard Shortcuts",
    "noKeyboardShortcuts": "No Keyboard Shortcuts",
    "stembleDashboard": "Stemble Dashboard",
    "helpAndInfo": "Help & Info",
    "resources": "Resources",
    "mainNavigation": "App"
  },
  "fr": {
    "closeSidebar": "Fermer la barre latérale",
    "openSidebar": "Ouvrir la barre latérale",
    "keyboardShortcuts": "Raccourcis du clavier",
    "noKeyboardShortcuts": "Aucun raccourci du clavier",
    "stembleDashboard": "Tableau de bord de Stemble",
    "helpAndInfo": "Aide & Informations",
    "resources": "Ressources",
    "mainNavigation": "App"
  }
}
</i18n>
